<script setup lang="ts">
import { useData } from 'vike-vue/useData'
import { useI18n } from 'vue-i18n'
import type { ContactPageData } from './+data'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'

const data = useData<ContactPageData>()
useFormatSeoContent(data.contact.seoTitle, data.contact.hero.subtitle)
useGtmOnPageMounted('other')

const { t } = useI18n()

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        data.contact?.breadcrumbTitle && data.contact.breadcrumbTitle.length > 40
          ? `${data.contact?.breadcrumbTitle.slice(0, 40)}...`
          : data.contact?.breadcrumbTitle,
    },
  ],
}
</script>

<template>
  <div
    v-if="data.contact"
    class="Contact"
  >
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="Contact__breadcrumb"
    />
    <organisms-ui-hero v-bind="data.contact.hero" />
    <div class="container my-8">
      <section class="Contact__modes mb-8">
        <h2 class="Contact__modes__title heading-2 color-blue-01">{{ data.contact.modesBlock.title }}</h2>
        <div
          v-if="
            data.contact.modesBlock.blockPhone ||
            data.contact.modesBlock.blockRequest ||
            data.contact.modesBlock.blockChat
          "
          class="Contact__modes__list grid g-4"
        >
          <molecules-ui-contact
            v-if="data.contact.modesBlock.blockPhone"
            class="Contact__modes__mode"
            v-bind="data.contact.modesBlock.blockPhone"
          />
          <molecules-ui-highlight
            v-if="data.contact.modesBlock.blockRequest"
            class="Contact__modes__mode"
            v-bind="data.contact.modesBlock.blockRequest"
          />
          <molecules-ui-highlight
            v-if="data.contact.modesBlock.blockChat"
            class="Contact__modes__mode"
            v-bind="data.contact.modesBlock.blockChat"
          />
        </div>
      </section>
      <section
        v-if="data.contact.blockFaq"
        class="grid mb-8"
      >
        <molecules-ui-bevel
          class="Contact__faq"
          v-bind="data.contact.blockFaq"
        />
      </section>
      <section
        v-if="data.contact.actionBlock"
        class="grid g-4"
      >
        <molecules-ui-card
          v-for="item in data.contact.actionBlock"
          :key="item.id"
          v-bind="item"
          class="Contact__card"
        />
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'utilities/mq';

@forward 'utilities/text';

.Contact {
  :deep(.Highlight__title) {
    @extend .heading-3;
  }
}

.Contact__breadcrumb {
  position: absolute;
  top: 0;
}

.Contact__modes__title {
  grid-column: 1 / -1;
  margin-block-end: map.get(spaces.$sizes, '5');
  text-align: center;

  @include mq.media('>=small') {
    margin-block-end: map.get(spaces.$sizes, '6');
  }
}

.Contact__modes__mode {
  grid-column: span 4;

  @include mq.media('<medium') {
    &:nth-child(2) {
      order: 1;
    }

    &:first-child {
      order: 2;
    }

    &:last-child {
      order: 3;
    }
  }
}

.Contact__faq {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 12;
  }

  @include mq.media('>=large') {
    grid-column: 2 / span 10;
  }
}

.Contact__card {
  grid-column: span 4;
}
</style>
